import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Activity, ShieldCheck, ShieldAlert, GraduationCap, Loader2, Cpu } from "lucide-react";

const STATUS = {
  auto: { label: "Auto", cls: "bg-signal-success/15 text-signal-success", bar: "bg-signal-success", icon: ShieldCheck },
  learning: { label: "Apprentissage", cls: "bg-signal-processing/15 text-signal-processing", bar: "bg-signal-processing", icon: GraduationCap },
  manual: { label: "Assisté", cls: "bg-signal-error/15 text-signal-error", bar: "bg-signal-error", icon: ShieldAlert },
};

export const ChecksumCoverage = ({ compact = false }) => {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/admin/checksum/coverage").then(({ data }) => setD(data)).catch(() => {}); }, []);

  if (!d) return <div className="glass rounded-xl p-8 flex items-center justify-center gap-2 font-mono text-xs text-zinc-500"><Loader2 className="w-4 h-4 animate-spin" /> Analyse de la couverture…</div>;

  const pct = d.coverage_pct;
  const R = 52, C = 2 * Math.PI * R, off = C - (pct / 100) * C;

  return (
    <div className="glass rounded-xl p-6 lg:p-8 relative overflow-hidden" data-testid="checksum-coverage">
      <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-aurora-cyan/10 blur-3xl pointer-events-none" />
      <div className="relative flex flex-col lg:flex-row gap-8">
        {/* Left — big gauge */}
        <div className="lg:w-64 shrink-0">
          <div className="flex items-center gap-2 mb-4">
            <Activity className="w-4 h-4 text-aurora-cyan" />
            <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-400">Couverture auto-livraison</span>
          </div>
          <div className="flex items-center gap-5">
            <div className="relative w-32 h-32 shrink-0">
              <svg className="w-32 h-32 -rotate-90" viewBox="0 0 120 120">
                <circle cx="60" cy="60" r={R} fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="8" />
                <circle cx="60" cy="60" r={R} fill="none" stroke="url(#ccg)" strokeWidth="8" strokeLinecap="round"
                  strokeDasharray={C} strokeDashoffset={off} style={{ transition: "stroke-dashoffset 1s ease" }} />
                <defs>
                  <linearGradient id="ccg" x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0%" stopColor="#22d3ee" /><stop offset="100%" stopColor="#a78bfa" />
                  </linearGradient>
                </defs>
              </svg>
              <div className="absolute inset-0 grid place-items-center">
                <span className="font-display text-4xl font-black tracking-tighter text-transparent bg-clip-text bg-gradient-to-br from-white via-aurora-cyan to-aurora-purple">{pct}<span className="text-lg">%</span></span>
              </div>
            </div>
            <div className="space-y-1.5">
              <div><div className="font-display text-2xl font-bold text-signal-success leading-none">{d.families_auto}</div><div className="font-mono text-[9px] uppercase tracking-widest text-zinc-500">familles auto</div></div>
              <div><div className="font-display text-2xl font-bold text-signal-processing leading-none">{d.families_learning}</div><div className="font-mono text-[9px] uppercase tracking-widest text-zinc-500">en apprentissage</div></div>
              <div><div className="font-display text-2xl font-bold text-white leading-none">{d.solutions_auto}<span className="text-zinc-600">/{d.solutions_total}</span></div><div className="font-mono text-[9px] uppercase tracking-widest text-zinc-500">solutions auto</div></div>
            </div>
          </div>
        </div>

        {/* Right — per-family list */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between mb-3">
            <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-400">Familles ECU · {d.families_total}</span>
            <span className="font-mono text-[9px] uppercase tracking-widest text-zinc-600">alimentez le moteur → Outils</span>
          </div>
          <div className={`space-y-2 ${compact ? "max-h-52" : "max-h-72"} overflow-y-auto pr-1`}>
            {d.families.map((f, i) => {
              const m = STATUS[f.status];
              const Icon = m.icon;
              const w = f.status === "auto" ? 100 : f.status === "learning" ? Math.min(50 + f.samples * 20, 90) : 12;
              return (
                <div key={i} data-testid={`coverage-fam-${i}`} className="flex items-center gap-3 bg-black/20 border border-white/5 rounded-sm px-3 py-2">
                  <Cpu className="w-3.5 h-3.5 text-zinc-500 shrink-0" />
                  <span className="font-mono text-xs text-white truncate w-40 shrink-0">{f.ecu}</span>
                  <div className="flex-1 h-1.5 rounded-full bg-white/5 overflow-hidden"><div className={`h-full rounded-full ${m.bar}`} style={{ width: `${w}%` }} /></div>
                  <span className="font-mono text-[9px] text-zinc-500 shrink-0">{f.solutions} sol.</span>
                  <span className={`font-mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-sm shrink-0 flex items-center gap-1 ${m.cls}`}><Icon className="w-3 h-3" />{m.label}</span>
                </div>
              );
            })}
            {d.families.length === 0 && <div className="font-mono text-xs text-zinc-600 py-6 text-center">Aucune solution en bibliothèque.</div>}
          </div>
        </div>
      </div>
    </div>
  );
};
